import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import ClientLayout from "../../../components/client/ClientLayout";
import PasswordInput from "../../../components/PasswordInput";
import { clientApi } from "../../../api";

/**
 * IGT-only staff tool — rotate a member's PIN via MeRS
 * (PUT /membership_numbers/{n}/membership_card).
 *
 * Staff enters the member's current PIN + new PIN on their behalf at
 * the counter. The backend gates this endpoint to IGT venues; the
 * nav link hides for S7000 venues so staff never see it there.
 */
const REASON_TEXT = {
  incorrect_old_pin:        "That doesn't match the member's current PIN. Please try again.",
  encryption_error:         "Security check failed. Please try again.",
  invalid_pin_format:       "PIN must be 3–10 digits.",
  invalid_old_pin_format:   "Current PIN must be 3–10 digits.",
  invalid_new_pin_format:   "New PIN must be 3–10 digits.",
  invalid_pin_length:       "PIN length is invalid for this venue.",
  nonce_reused:             "Please wait a moment and try again.",
  prefix_reused:            "Please wait a moment and try again.",
};

export default function IgtChangePin() {
  const navigate = useNavigate();
  const [memberNumber, setMemberNumber] = useState("");
  const [oldPin, setOldPin]             = useState("");
  const [newPin, setNewPin]             = useState("");
  const [confirm, setConfirm]           = useState("");
  const [busy, setBusy]                 = useState(false);
  const [error, setError]               = useState("");
  const [success, setSuccess]           = useState(false);

  const canSubmit =
       !!memberNumber.trim()
    && /^\d{3,10}$/.test(oldPin)
    && /^\d{3,10}$/.test(newPin)
    && newPin === confirm
    && oldPin !== newPin;

  async function submit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError("");
    try {
      await clientApi.igt.changePin(memberNumber.trim(), oldPin, newPin);
      setSuccess(true);
    } catch (err) {
      const raw = String(err?.message || "");
      const known = Object.keys(REASON_TEXT).find((k) => raw.includes(k));
      if (known) setError(REASON_TEXT[known]);
      else if (raw && raw.length < 200) setError(raw);
      else setError("Couldn't change PIN. Please try again.");
    }
    setBusy(false);
  }

  function clear() {
    setMemberNumber(""); setOldPin(""); setNewPin(""); setConfirm("");
    setError(""); setSuccess(false);
  }

  return (
    <ClientLayout>
      <div className="venues-page-header">
        <div>
          <h1>Change member PIN</h1>
          <p className="page-subtitle">IGT — rotate a member's PIN using their current PIN</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 460px) minmax(0, 1fr)", gap: "1.5rem", alignItems: "start" }}>
        <form onSubmit={submit} className="settings-card">
          <div className="settings-card-header">
            <h3>New PIN</h3>
            <p>Enter the member's current PIN to authorise the change.</p>
          </div>

          {error   && <div className="toast toast-error"   style={{ marginBottom: "0.75rem" }}>{error}</div>}
          {success && <div className="toast toast-success" style={{ marginBottom: "0.75rem" }}>✓ PIN changed successfully.</div>}

          <div className="form-field">
            <label>Membership number <span className="required">*</span></label>
            <input
              value={memberNumber}
              onChange={(e) => setMemberNumber(e.target.value.replace(/\s+/g, ""))}
              placeholder="e.g. 6050"
              inputMode="numeric"
              autoFocus
              disabled={success}
            />
          </div>

          <div className="form-field">
            <label>Current PIN <span className="required">*</span></label>
            <PasswordInput
              value={oldPin}
              onChange={(e) => setOldPin(e.target.value.replace(/\D/g, "").slice(0, 10))}
              maxLength={10}
              inputMode="numeric"
              placeholder="3–10 digits"
              autoComplete="off"
              disabled={success}
            />
          </div>

          <div className="form-field">
            <label>New PIN <span className="required">*</span></label>
            <PasswordInput
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 10))}
              maxLength={10}
              inputMode="numeric"
              placeholder="3–10 digits"
              autoComplete="new-password"
              disabled={success}
            />
          </div>

          <div className="form-field">
            <label>Confirm new PIN <span className="required">*</span></label>
            <PasswordInput
              value={confirm}
              onChange={(e) => setConfirm(e.target.value.replace(/\D/g, "").slice(0, 10))}
              maxLength={10}
              inputMode="numeric"
              placeholder="3–10 digits"
              autoComplete="new-password"
              disabled={success}
            />
          </div>

          {newPin && confirm && newPin !== confirm && (
            <div className="toast toast-error" style={{ marginBottom: "0.5rem" }}>New PINs don't match.</div>
          )}
          {oldPin && newPin && oldPin === newPin && (
            <div className="toast toast-error" style={{ marginBottom: "0.5rem" }}>New PIN must differ from current PIN.</div>
          )}

          <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
            {!success ? (
              <>
                <button type="submit" className="btn btn-primary" disabled={busy || !canSubmit}>
                  {busy ? "Updating…" : "Change PIN"}
                </button>
                <button type="button" className="btn btn-outline" onClick={clear}>Clear</button>
              </>
            ) : (
              <>
                <button type="button" className="btn btn-primary" onClick={clear}>Change another PIN</button>
                <button type="button" className="btn btn-outline" onClick={() => navigate("/client/members")}>Back to members</button>
              </>
            )}
          </div>
        </form>

        <div>
          <div className="empty-state" style={{ borderStyle: "dashed" }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" width="48" height="48">
              <rect x="3" y="11" width="18" height="11" rx="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
            <h3>How this works</h3>
            <p>
              IGT requires the member to prove identity with their current PIN before it can be changed.
              The PIN is encrypted with the venue's session key and never stored on our side.
              A successful change is logged to Activity Logs as <code>pin_change</code>.
            </p>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
}
